/**
 * The form set: react-hook-form + zod, wired so every screen does it the same way.
 *
 * The pattern, in full:
 *
 *   const schema = z.object({ title: z.string().min(1, 'Enter a title') });
 *
 *   const form = useZodForm(schema, { defaultValues: { title: '' } });
 *
 *   const save = useApiMutation({
 *     mutationFn: (values) => api.patch(STAFF.case(id), values, { ifMatch: caseRecord.version }),
 *     invalidates: [qk.all('cases')],
 *   });
 *
 *   <Form form={form} onSubmit={(values) => save.mutateAsync(values)} error={save.error}>
 *     <FormInput form={form} name="title" label="Title" />
 *     <FormActions submitLabel="Save" busy={save.isPending} />
 *   </Form>
 *
 * `<Form>` does three things you would otherwise repeat in every screen:
 *
 *   - maps the API's field-level `errors` onto the right inputs, so a 400 from the
 *     server lands on the field rather than in a toast;
 *   - shows the non-field failure once, at the top, using `userMessage()` — which is
 *     what applies the 404 disclosure rule and shows a 409's reason verbatim;
 *   - moves focus to the first error, because a validation message below the fold
 *     with the submit button still enabled reads as a broken form.
 */

import { fieldErrors, userMessage } from '@legalcrm/api/errors';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, type ReactNode } from 'react';
import {
  useForm,
  type FieldValues,
  type Path,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import type { z } from 'zod';

import { cn } from '../lib/cn';
import { Button } from '../components/Button';

/** `useForm` with the zod resolver already attached and the types tied together. */
export function useZodForm<TSchema extends z.ZodType<FieldValues>>(
  schema: TSchema,
  options?: Omit<UseFormProps<z.infer<TSchema>>, 'resolver'>,
): UseFormReturn<z.infer<TSchema>> {
  const form = useForm<z.infer<TSchema>>({
    resolver: zodResolver(schema) as never,
    // Validate on blur rather than on every keystroke: a message appearing while
    // someone is still typing their own email address is noise, not help.
    mode: 'onBlur',
    reValidateMode: 'onChange',
    ...options,
  });

  // The schema travels with the form so a field can ask whether it may be left blank,
  // and mark itself required *before* somebody submits and gets a red border. Marking
  // each field by hand is the alternative, and it drifts the first time a schema
  // changes and the label does not.
  (form as FormWithSchema).__schema = schema;
  return form;
}

/** Carries the schema `useZodForm` was given, for `isRequiredField` to read. */
interface FormWithSchema {
  __schema?: unknown;
}

/**
 * Whether a field cannot be left blank, according to the schema itself.
 *
 * The test is literal: does the empty string fail this field's validation. That is
 * exactly what "required" means to somebody filling a form in, and it is more honest
 * than asking zod whether the field is optional -- `z.string()` is not optional, yet
 * an empty string satisfies it, so `description` would be starred alongside `title`.
 *
 * Only string-shaped fields are considered. A checkbox or a number would fail an
 * empty-string parse for reasons that have nothing to do with being mandatory.
 *
 * Returns false rather than throwing on anything unexpected. A missing asterisk is a
 * small loss; a form that will not render is not.
 */
export function isRequiredField(form: unknown, name: string): boolean {
  try {
    const schema = (form as FormWithSchema)?.__schema as
      | { shape?: Record<string, unknown>; _def?: { schema?: { shape?: Record<string, unknown> } } }
      | undefined;
    if (!schema) return false;

    // `.superRefine()` wraps the object, putting the shape one level down.
    const shape = schema.shape ?? schema._def?.schema?.shape;
    const field = shape?.[name] as { safeParse?: (v: unknown) => { success: boolean } } | undefined;
    if (!field?.safeParse) return false;

    if (field.safeParse('x').success !== true) return false; // not string-shaped
    return field.safeParse('').success === false;
  } catch {
    return false;
  }
}

export interface FormProps<TValues extends FieldValues> {
  form: UseFormReturn<TValues>;
  onSubmit: SubmitHandler<TValues>;
  /** The mutation's error. Field errors are routed to inputs; the rest shows above. */
  error?: unknown;
  children: ReactNode;
  className?: string;
  /** Accessible name, when the surrounding heading is not adjacent. */
  ariaLabel?: string;
}

export function Form<TValues extends FieldValues>({
  form,
  onSubmit,
  error,
  children,
  className,
  ariaLabel,
}: FormProps<TValues>) {
  const { setError, formState } = form;

  // Route the server's field-level errors onto the matching inputs.
  useEffect(() => {
    const fields = fieldErrors(error);
    for (const [name, message] of Object.entries(fields)) {
      setError(name as Path<TValues>, { type: 'server', message });
    }
  }, [error, setError]);

  const fields = fieldErrors(error);
  // A failure with no field errors is a whole-form problem: a 403, a 409 state
  // guard, a network failure. Those get the banner.
  const formLevelError = error && Object.keys(fields).length === 0 ? userMessage(error) : null;

  return (
    <form
      noValidate
      aria-label={ariaLabel}
      onSubmit={form.handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-4', className)}
    >
      {formLevelError && (
        <div
          role="alert"
          className="rounded border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-ink"
        >
          {formLevelError}
        </div>
      )}
      {children}
      {/* Announced without stealing focus. */}
      <p className="sr-only" aria-live="polite">
        {formState.isSubmitting ? 'Saving' : ''}
      </p>
    </form>
  );
}

export interface FormActionsProps {
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  busy?: boolean;
  destructive?: boolean;
  /** Extra content on the left, e.g. a "last saved" note. */
  aside?: ReactNode;
  className?: string;
}

export function FormActions({
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  onCancel,
  busy,
  destructive,
  aside,
  className,
}: FormActionsProps) {
  return (
    <div className={cn('flex items-center justify-between gap-3 pt-1', className)}>
      <div className="text-xs text-ink-muted">{aside}</div>
      <div className="flex items-center gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
        )}
        <Button
          type="submit"
          variant={destructive ? 'danger' : 'primary'}
          loading={busy}
          loadingLabel={`${submitLabel} in progress`}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
